/**
 * Помилки телефонного флоу: витяг коду + текст для людини (REFACTOR_PLAN п.17).
 *
 * Модуль ЧИСТИЙ навмисно — ні Firebase SDK, ні DOM, ні Sentry. Це дозволяє
 * покрити тестами саме ту частину, яка найтихіше ламається при рефакторі:
 * розбір форми помилки. Все, що потребує браузера (детект in-app-браузера),
 * приходить сюди параметром.
 *
 * Раніше ця логіка жила лише в `stores/auth.ts`, а друга копія флоу
 * (`PhoneOtpModal.vue`) мала замість неї ДВА захардкоджені рядки — тож людина,
 * що міняла номер у профілі, на `auth/too-many-requests` бачила «Не вдалося
 * надіслати код. Спробуйте пізніше» і не дізнавалась, що заблоковано НОМЕР.
 */

/**
 * Помилка Firebase у тому обсязі, який тут читається. Звужуємо явно, а не
 * через FirebaseError: форма відрізняється між збірками SDK — саме тому
 * нижче стільки альтернативних шляхів до тексту помилки.
 */
export type FirebaseErrorLike =
  | {
      code?: unknown;
      message?: unknown;
      customData?: {
        message?: unknown;
        _tokenResponse?: {
          error?: {
            message?: unknown;
            errors?: Array<{ message?: unknown }>;
          };
        };
      } | null;
    }
  | null
  | undefined;

/**
 * Помилка телефонного флоу з РОЗДІЛЕНИМИ повідомленнями.
 *
 * `message` — машинний код Firebase: Sentry групує за ним, і група лишається
 * стабільною. Раніше назовні летів український текст із доклеєним «(код: …)»,
 * тож кожне формулювання створювало окрему групу, а код доводилось вигрібати
 * з тегів. `userMessage` — те, що бачить людина.
 */
export class PhoneAuthError extends Error {
  readonly userMessage: string;

  constructor(code: string, userMessage: string, cause?: unknown) {
    super(code || "auth/unknown", { cause });
    this.name = "PhoneAuthError";
    this.userMessage = userMessage;
  }
}

/** Найкорисніший текст помилки: форма залежить від збірки SDK. */
export function extractPhoneAuthError(e: unknown): string {
  const err = e as FirebaseErrorLike;
  // String() у кінці, а не по гілках: обидва виклики і так коерсили результат.
  return String(
    err?.customData?._tokenResponse?.error?.message ||
      err?.customData?._tokenResponse?.error?.errors?.[0]?.message ||
      err?.customData?.message ||
      err?.message ||
      err?.code ||
      "Unknown error",
  );
}

/**
 * Канонічний код `auth/*`.
 *
 * Частина помилок несе код лише всередині тексту повідомлення, тому після
 * прямих полів іде регулярка. Копія в `PhoneOtpModal` читала самий `e.code` —
 * і тег `firebase_code` у Sentry для цього флоу часто лишався порожнім.
 */
export function getFirebaseAuthCode(e: unknown): string {
  const err = e as FirebaseErrorLike;
  const direct = err?.code;
  const tokenMsg = err?.customData?._tokenResponse?.error?.message;
  const raw = String(direct || tokenMsg || err?.message || "");

  const m = raw.match(/(auth\/[a-z0-9-]+|auth\/error-code:-?\d+)/i);
  return String(direct || (m ? m[1] : tokenMsg) || "");
}

/**
 * Текст для людини.
 *
 * `inAppBrowser` приходить параметром, щоб модуль лишався чистим: детект
 * читає `navigator.userAgent`, якого в тестах і на сервері немає.
 */
export function friendlyPhoneErrorMessage(
  e: unknown,
  inAppBrowser = false,
): string {
  const raw = extractPhoneAuthError(e);
  const code = String(getFirebaseAuthCode(e) || "");
  const text = String(raw || "");

  // Короткий код підтримки для випадків, де він реально допомагає (без PII).
  const withSupportCode = (msg: string): string => {
    const c = code || "unknown";
    return `${msg} (код: ${c})`;
  };

  if (text.includes("appVerificationDisabledForTesting")) {
    return withSupportCode(
      "Не вдалося ініціалізувати перевірку (reCAPTCHA). Оновіть сторінку та спробуйте ще раз.",
    );
  }

  if (code.includes("auth/error-code:-39") || text.includes("error-code:-39")) {
    if (inAppBrowser) {
      return withSupportCode(
        "Вбудований браузер (Instagram/Facebook) блокує перевірку безпеки. Натисніть ⋯ → «Відкрити у браузері» або скопіюйте посилання в Chrome/Safari.",
      );
    }
    return withSupportCode(
      "Не вдалося пройти перевірку безпеки (reCAPTCHA). Спробуйте ще раз. Якщо проблема повторюється — вимкніть VPN/AdBlock/Private Relay або спробуйте інший браузер.",
    );
  }

  if (code.includes("auth/invalid-verification-code")) {
    return "Невірний код з SMS. Перевірте та спробуйте ще раз.";
  }
  if (
    code.includes("auth/code-expired") ||
    code.includes("auth/session-expired")
  ) {
    return "Термін дії коду минув. Натисніть «Отримати код» ще раз.";
  }

  // Ліміти. Формулювання ВАЖЛИВЕ: блок висить на НОМЕРІ, а не на з'єднанні.
  // Попередній текст радив «спробуйте з іншого інтернету» — це неправда, і
  // людина витрачала спробу дарма (REFACTOR_PLAN п.17, етап 5).
  if (code.includes("auth/too-many-requests")) {
    return "Забагато запитів коду на цей номер — Firebase тимчасово його заблокував. Зміна браузера чи мережі не допоможе: почекайте 30–60 хвилин або увійдіть через email.";
  }
  if (code.includes("auth/quota-exceeded")) {
    return "Тимчасово недоступно (ліміт SMS). Спробуйте пізніше або увійдіть через email.";
  }

  // Зміна номера/способу входу — операція, для якої Firebase вимагає свіжий
  // вхід. Код був не змаплений, і людина бачила сирий текст ПІСЛЯ того, як
  // SMS уже витрачена.
  if (code.includes("auth/requires-recent-login")) {
    return "З міркувань безпеки увійдіть заново, а потім змініть номер.";
  }

  if (code.includes("auth/invalid-phone-number")) {
    return "Некоректний номер телефону. Перевірте формат +380 XX XXX XX XX.";
  }
  if (code.includes("auth/missing-phone-number")) {
    return "Вкажіть номер телефону.";
  }

  // Помилки конфігурації (домен / ключ / вимкнений провайдер).
  if (code.includes("auth/unauthorized-domain")) {
    return withSupportCode(
      "Сайт відкритий з неавторизованого домену. Відкрийте основний домен або оновіть сторінку.",
    );
  }
  if (code.includes("auth/operation-not-allowed")) {
    return withSupportCode(
      "Авторизація телефоном тимчасово вимкнена на сервері. Спробуйте пізніше.",
    );
  }
  if (
    code.includes("auth/app-not-authorized") ||
    code.includes("auth/invalid-api-key")
  ) {
    return withSupportCode(
      "Помилка конфігурації авторизації. Спробуйте пізніше.",
    );
  }

  // reCAPTCHA / app credential часто ламають VPN/AdBlock/Private Relay.
  if (code.includes("auth/captcha-check-failed")) {
    return "Не вдалося пройти перевірку (reCAPTCHA). Оновіть сторінку та спробуйте ще раз.";
  }
  if (
    code.includes("auth/invalid-app-credential") ||
    code.includes("auth/missing-app-credential")
  ) {
    return withSupportCode(
      "Не вдалося пройти перевірку (reCAPTCHA). Вимкніть AdBlock/VPN/Private Relay або спробуйте інший браузер.",
    );
  }

  if (code.includes("auth/network-request-failed")) {
    return "Проблема з мережею. Перевірте інтернет або вимкніть VPN/Private Relay та спробуйте ще раз.";
  }

  if (code.includes("auth/internal-error") || code.includes("auth/internal")) {
    return withSupportCode(
      "Внутрішня помилка сервісу. Спробуйте ще раз трохи пізніше.",
    );
  }

  if (text.startsWith("Firebase:")) {
    return withSupportCode("Сталася помилка. Спробуйте ще раз.");
  }

  return withSupportCode(text || "Сталася помилка. Спробуйте ще раз.");
}

/** Чи помилка вбиває сесію підтвердження — тоді потрібен новий код. */
export function shouldResetConfirmation(e: unknown): boolean {
  const code = String(getFirebaseAuthCode(e) || "");
  return (
    code.includes("auth/code-expired") ||
    code.includes("auth/session-expired") ||
    code.includes("auth/missing-verification-code")
  );
}
